import type { NormalizedRoundSchedule, RoundName } from "../../domain/round/types";
import type { TransportPlan } from "../../domain/transport/types";

export type VariantContent = {
  id: string;
  storageId: string;
  schema: string;
  label: string;
  variantSource: string;
  createdAt: string;
  createdBy: string;
  confirmedSnapshot: boolean;
  schedule: NormalizedRoundSchedule;
  transport: TransportPlan | null;
  transportStale: boolean;
  validation: { valid: boolean; blockers: Array<{ message: string }>; warnings: Array<{ message: string }> };
};

async function jsonCall<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, { credentials: "include", ...init, headers: { Accept: "application/json", ...(init?.headers || {}) } });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = body && typeof body === "object" && "detail" in body ? (body as { detail?: unknown }).detail : null;
    if (typeof detail === "string") throw new Error(detail);
    if (detail && typeof detail === "object" && "message" in detail) throw new Error(String((detail as { message?: unknown }).message || `HTTP ${response.status}`));
    throw new Error(`HTTP ${response.status}`);
  }
  return body as T;
}

export async function loadVariantContent(tournamentId: string, round: RoundName, variantId: string) {
  const result = await jsonCall<{ variant: VariantContent }>(`/tournaments/${encodeURIComponent(tournamentId)}/rounds/${round}/variants/${encodeURIComponent(variantId)}/content`);
  return result.variant;
}

export async function trashConfirmedRound(tournamentId: string, round: RoundName) {
  return jsonCall<{ ok: boolean; trash: { id: string; kind: string; label: string; deletedAt: string } }>(`/tournaments/${encodeURIComponent(tournamentId)}/rounds/${round}/confirmed`, { method: "DELETE" });
}

export type VariantComparison = {
  leftLabel: string;
  rightLabel: string;
  changedAssignments: number;
  leftOnly: string[];
  rightOnly: string[];
  peopleChanged: number;
  departureDelta: number;
  returnDelta: number;
  leftValidation: string;
  rightValidation: string;
};

function assignmentRows(schedule: NormalizedRoundSchedule) {
  const groups = new Map(schedule.groups.map((group) => [group.id, group.number]));
  const people = new Map(schedule.people.map((person) => [person.personId, person.displayName]));
  return schedule.assignments.map((assignment) => ({
    key: `${groups.get(assignment.groupId) || assignment.groupId}|${assignment.segment}|${assignment.personId}`,
    personId: assignment.personId,
    text: `${people.get(assignment.personId) || assignment.personId} · G${groups.get(assignment.groupId) || "?"} · ${assignment.segment}${assignment.locked ? " · LOCK" : ""}`,
  }));
}

function validationLabel(value: VariantContent["validation"] | undefined) {
  if (!value) return "brak danych";
  return value.valid ? `OK · ${value.warnings?.length || 0} ostrz.` : `${value.blockers?.length || 0} blockerów · ${value.warnings?.length || 0} ostrz.`;
}

export function compareVariantContent(
  left: { label: string; schedule: NormalizedRoundSchedule; transport: TransportPlan | null; validation?: VariantContent["validation"] },
  right: { label: string; schedule: NormalizedRoundSchedule; transport: TransportPlan | null; validation?: VariantContent["validation"] },
): VariantComparison {
  const leftRows = assignmentRows(left.schedule);
  const rightRows = assignmentRows(right.schedule);
  const leftKeys = new Set(leftRows.map((row) => row.key));
  const rightKeys = new Set(rightRows.map((row) => row.key));
  const leftOnlyRows = leftRows.filter((row) => !rightKeys.has(row.key));
  const rightOnlyRows = rightRows.filter((row) => !leftKeys.has(row.key));
  const changedPeople = new Set([...leftOnlyRows, ...rightOnlyRows].map((row) => row.personId));
  return {
    leftLabel: left.label,
    rightLabel: right.label,
    changedAssignments: leftOnlyRows.length + rightOnlyRows.length,
    leftOnly: leftOnlyRows.slice(0, 30).map((row) => row.text),
    rightOnly: rightOnlyRows.slice(0, 30).map((row) => row.text),
    peopleChanged: changedPeople.size,
    departureDelta: (right.transport?.departures.length || 0) - (left.transport?.departures.length || 0),
    returnDelta: (right.transport?.returns.length || 0) - (left.transport?.returns.length || 0),
    leftValidation: validationLabel(left.validation),
    rightValidation: validationLabel(right.validation),
  };
}
